import { useEffect, useRef, useState } from "react";
import Icon from "../../../designSystem/a-atoms/icons/Icon";
import style from "./Cell.module.css";
import { ref, remove, update } from "firebase/database";
import { db } from "../../../configs/Firebase";
import StyleSwatch from "./StyleSwatch";
import Text from "../cells/Text";
import Quote from "../cells/Quote";
import Picture from "../cells/Picture";
import Phases from "../cells/Phases";
import Funnel from "../cells/Funnel";
import Emotion from "../cells/Emotion";
import Categories from "../cells/Categories";
import BarChart from "../cells/BarChart";
import Time from "../cells/Time";
import StepNumber from "../cells/StepNumber";
import FunnelV2 from "../cells/FunnelV2";
import Scoring from "../cells/Scoring";
import Figma from "../cells/Figma";

const Cell = (props) => {
  const cellWidth = 240;
  const [span, setSpan] = useState(0);
  const [mouseXPostion, setMouseXPostion] = useState(0);
  const [startingColspan, setStartingColspan] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);
  const [dragHandleSide, setDragHandleSide] = useState(null); // 'left' or 'right'
  const [visibleButtons, setVisibleButtons] = useState({
    left: false,
    right: false,
    above: false,
    below: false,
  });
  const [showToolbar, setShowToolbar] = useState(false);
  const [showCommentIcon, setShowCommentIcon] = useState(false);
  const cellRef = useRef(null);

  useEffect(() => {
    setSpan(props.data.span);
  }, [props.data.span]);

  function onDragStart(e, data, side) {
    var crt = document.createElement("img");
    // crt.style.backgroundColor = "red";
    e.dataTransfer.setDragImage(crt, 0, 0);

    setMouseDown(true);
    setDragHandleSide(side);
    setMouseXPostion(e.clientX || e.pageX);
    if (data.span && data.span !== undefined) {
      setStartingColspan(data.span);
    } else {
      setStartingColspan(1);
    }
  }
  function onDrag(e, data) {
    // e.preventDefault();
    let xPos = e.clientX || e.pageX;

    if (startingColspan && xPos) {
      let difference = xPos - mouseXPostion;

      // For left handle, invert the difference since we're moving the left edge
      if (dragHandleSide === 'left') {
        difference = -difference;
      }

      let newSpan = startingColspan + difference / cellWidth;
      if (newSpan < 1) {
        newSpan = 1;
      }
      setSpan(newSpan);
    }
    e.preventDefault();
  }

  function onDragEnd(e, data) {
    setMouseDown(false);
    let xPos = e.clientX || e.pageX;

    let difference = xPos - mouseXPostion;

    // For left handle, invert the difference since we're moving the left edge
    if (dragHandleSide === 'left') {
      difference = -difference;
    }

    let newSpan = startingColspan + Math.round(difference / cellWidth);
    if (newSpan < 1) {
      newSpan = 1;
    }
    setSpan(newSpan);
    setStartingColspan(false);
    setDragHandleSide(null);
    update(ref(db), {
      [props.journeyRefURL +
        "/rows/" +
        props.row.id +
        "/columns/" +
        props.cellIndex +
        "/span"]: newSpan,
    });
  }


  function addCell(e, rowIndex, cellIndex, dir, cellRef, rowRef) {
    if (props.type === "emotion") {
      props.copyAndPaste(e, rowIndex, cellIndex, dir, cellRef, rowRef, 4);
    } else {
      props.copyAndPaste(e, rowIndex, cellIndex, dir, cellRef, rowRef);
    }
  }

  function handleAddCellClick(e, direction) {
    e.preventDefault();
    e.stopPropagation();
    // Pass the current cellIndex - copyAndPaste will handle the logic for where to copy from and insert
    addCell(e, props.rowIndex, props.cellIndex, direction, props.data.id, props.row.id);
  }
  function deleteCell(e, rowIndex, cellIndex, dir, cellRef, rowRef) {
    if (
      window.confirm("Are you sure you want to delete this cell?  You will not be able to undo this action")
    ) {
      props.deleteCell(e, rowIndex, cellIndex, dir, cellRef, rowRef);
    }
  }

  const [cellStyle, setCellStyle] = useState(
    props.data.style ? props.data.style : "default"
  );
  function changeStyle(val) {
    update(ref(db), {
      [props.journeyRefURL +
        "/rows/" +
        props.row.id +
        "/columns/" +
        props.cellIndex +
        "/style"]: val,
    });
  }


  function handleMouseMove(e) {
    if (!props.editable || !cellRef.current) return;

    const rect = cellRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const cellWidth = rect.width;
    const cellHeight = rect.height;
    // Calculate percentage from each edge
    const leftPercent = (mouseX / cellWidth) * 100;
    const rightPercent = ((cellWidth - mouseX) / cellWidth) * 100;
    const topPercent = (mouseY / cellHeight) * 100;
    const bottomPercent = ((cellHeight - mouseY) / cellHeight) * 100;
    // Update visible buttons based on 25% threshold
    setVisibleButtons({
      left: leftPercent <= 25,
      right: rightPercent <= 25,
      above: topPercent <= 25,
      below: bottomPercent <= 25,
    });
    setShowToolbar(true);
  }

  function handleMouseLeave(e) {
    if (!props.editable) return;
    // If moving into an element inside this cell (e.g. the toolbar), do not hide
    // Check if relatedTarget is a valid Node before calling contains
    if (cellRef.current && e.relatedTarget && e.relatedTarget instanceof Node && cellRef.current.contains(e.relatedTarget)) {
      return;
    }
    setVisibleButtons({ left: false, right: false, above: false, below: false });
    setShowToolbar(false);
  }

  function handleCellHoverEnter() {
    if (!props.editable && props.onOpenComments) {
      setShowCommentIcon(true);
    }
  }

  function handleCellHoverLeave(e) {
    if (!props.editable) {
      // If moving into the comment icon, don't hide it
      if (cellRef.current && e.relatedTarget && e.relatedTarget instanceof Node && cellRef.current.contains(e.relatedTarget)) {
        return;
      }
      setShowCommentIcon(false);
    }
  }

  function handleCommentIconClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (props.onOpenComments) {
      // Use cellIndex for Firebase path since columns are stored as array
      props.onOpenComments(props.row.id, props.cellIndex);
    }
  }

  function handleToolbarDelete() {
    // Reuse existing deleteCell helper with the correct indices/ids
    deleteCell(
      null,
      props.rowIndex,
      props.cellIndex,
      "delete",
      props.data.id,
      props.row.id
    );
  }

  return (
    <>

      <div
        ref={cellRef}
        className={`${style.cell} ${mouseDown && style.mouseDown}`}
        style={{ "--span": span ? span : 1 }}
        data-cell={true}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleCellHoverEnter}
        onMouseLeave={(e) => {
          handleMouseLeave(e);
          handleCellHoverLeave(e);
        }}
      >
        {props.type === "stepNumber" ? (
          <StepNumber data={props.data.value} />
        ) : null}
        {props.type === "categories" ? (
          <Categories
            first={props.first}
            last={props.last}
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "emotion" ? (
          <Emotion
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            index={props.index}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "funnel" ? (
          <Funnel
            first={props.first}
            last={props.last}
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            index={props.index}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "funnelV2" ? (
          <FunnelV2
            first={props.first}
            last={props.last}
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            index={props.index}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "phases" ? (
          <Phases
            first={props.first}
            last={props.last}
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            style={cellStyle}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "picture" ? (
          <>
            <Picture
              journeyRefURL={props.journeyRefURL}
              data={props.data}
              cellIndex={props.cellIndex}
              row={props.row}
              editable={props.editable}
            // slideshow={slideshow}
            />
          </>
        ) : null}
        {props.type === "text" ? (
          <Text
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            onDelete={handleToolbarDelete}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "quote" ? (
          <Quote
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            onDelete={handleToolbarDelete}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "time" ? (
          <Time
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "barchart" ? (
          <BarChart
            first={props.first}
            last={props.last}
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "scoring" ? (
          <Scoring
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.type === "figma" ? (
          <Figma
            journeyRefURL={props.journeyRefURL}
            data={props.data}
            row={props.row}
            editable={props.editable}
            cellIndex={props.cellIndex}
          />
        ) : null}
        {props.editable ? (
          <>
            <span
              className={`${style.drag} ${style.dragLeft} ${visibleButtons.left ? style.visible : ''}`}
              onDragStart={(e) => onDragStart(e, props.data, 'left')}
              onDragOver={(e) => onDrag(e, props.data)}
              onDragEnd={(e) => onDragEnd(e, props.data)}
              draggable={true}
            >
              <span className={style.dragIndicator}></span>
              <button
                className={style.addButton}
                onClick={(e) => handleAddCellClick(e, 'left')}
                onMouseDown={(e) => e.stopPropagation()}
                type="button"
                aria-label="Add cell to the left"
              >
                <Icon>add</Icon>
              </button>
            </span>
            <span
              className={`${style.drag} ${visibleButtons.right ? style.visible : ''}`}
              onDragStart={(e) => onDragStart(e, props.data, 'right')}
              onDragOver={(e) => onDrag(e, props.data)}
              onDragEnd={(e) => onDragEnd(e, props.data)}
              draggable={true}
            >
              <span className={style.dragIndicator}></span>
              <button
                className={style.addButton}
                onClick={(e) => handleAddCellClick(e, 'right')}
                onMouseDown={(e) => e.stopPropagation()}
                type="button"
                aria-label="Add cell to the right"
              >
                <Icon>add</Icon>
              </button>
            </span>
            {props.onAddRowClick ? (
              <>
                <button
                  className={`${style.addRowButton} ${style.addRowButtonAbove} ${visibleButtons.above ? style.visible : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onAddRowClick(props.rowIndex, 'above');
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  type="button"
                  aria-label="Add row above"
                >
                  <Icon>add</Icon>
                </button>
                <button
                  className={`${style.addRowButton} ${style.addRowButtonBelow} ${visibleButtons.below ? style.visible : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onAddRowClick(props.rowIndex, 'below');
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  type="button"
                  aria-label="Add row below"
                >
                  <Icon>add</Icon>
                </button>
              </>
            ) : null}
            {/* <span className={style.more}>
              <Icon onClick={showOptionsHandler}>more_horiz</Icon>
            </span> */}
          </>
        ) : null}
        {props.editable && props.type !== "text" && props.type !== "quote" && showToolbar ? (
          <div className={style.cellToolbar}>
            {props.type === "phases" ? (
              <>
                <StyleSwatch
                  currentStyle={props.data.style || "style1"}
                  styleOptions={[
                    { id: 'style1', label: 'Phase style 1' },
                    { id: 'style2', label: 'Phase style 2' },
                    { id: 'style3', label: 'Phase style 3' },
                    { id: 'style4', label: 'Phase style 4' },
                  ]}
                  onStyleChange={changeStyle}
                  variant="phases"
                />
                <div className={style.toolbarSeparator}></div>
              </>
            ) : null}
            <button
              type="button"
              className={style.cellToolbarDelete}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToolbarDelete();
              }}
              onMouseDown={(e) => e.preventDefault()}
              title="Delete cell"
            >
              <Icon variants={["inheritColor"]}>close</Icon>
            </button>
          </div>
        ) : null}
        {!props.editable && (
          <>
            {(showCommentIcon || props.commentCount > 0) && (
              <div
                type="button"
                className={`${style.commentIcon} ${showCommentIcon ? style.visible : ''} ${props.commentCount > 0 ? style.hasComments : ''} ${props.unreadCount > 0 ? style.hasUnread : ''}`}
                onClick={handleCommentIconClick}
                onMouseEnter={() => setShowCommentIcon(true)}
                onMouseLeave={(e) => {
                  // Don't hide if there are comments
                  // if (props.commentCount === 0) {
                  //   setShowCommentIcon(false);
                  // }
                }}
                title="View comments"
              >
                
                {props.commentCount > 0 ? (
                  <span className={`${style.commentBadge} ${props.unreadCount > 0 ? style.hasUnread : ''}`}>{props.commentCount}</span>
                ) : (<Icon variants={["inheritColor"]}>add</Icon>)}
              </div>
            )}
          </>
        )}
      </div>
      {/* <Dropdown
        show={showOptions}
        style={dropDownPosition}
        onClose={hideOptionsHandler}
      >
        <div className={style.contextMenuContents}>
          {props.type === "phases" ? (
            <>
              <h4>Settings</h4>
              <ul>
                <li className={style.swatches}>
                  <span
                    className={style.phasesStyle1}
                    onClick={() => changeStyle("style1")}
                  >
                    A
                  </span>
                  <span
                    className={style.phasesStyle2}
                    onClick={() => changeStyle("style2")}
                  >
                    A
                  </span>
                  <span
                    className={style.phasesStyle3}
                    onClick={() => changeStyle("style3")}
                  >
                    A
                  </span>
                  <span
                    className={style.phasesStyle4}
                    onClick={() => changeStyle("style4")}
                  >
                    A
                  </span>
                </li>
              </ul>
            </>
          ) : null}

          {props.type === "text" ? (
            <>
              <h4>Settings</h4>
              <ul>
                <li className={style.swatches}>
                  <span
                    className={style.textStyle1}
                    onClick={() => changeStyle("style1")}
                  >
                    A
                  </span>
                  <span
                    className={style.textStyle2}
                    onClick={() => changeStyle("style2")}
                  >
                    A
                  </span>
                  <span
                    className={style.textStyle3}
                    onClick={() => changeStyle("style3")}
                  >
                    A
                  </span>
                  <span
                    className={style.textStyle4}
                    onClick={() => changeStyle("style4")}
                  >
                    A
                  </span>
                  <span
                    className={style.textStyle5}
                    onClick={() => changeStyle("style5")}
                  >
                    A
                  </span>
                </li>
              </ul>
            </>
          ) : null}

          <h4>Cells</h4>
          <ul>
            <li
              onClick={(e) =>
                addCell(
                  e,
                  props.rowIndex,
                  props.cellIndex,
                  "right",
                  props.data.id,
                  props.row.id
                )
              }
            >
              Add cell right
            </li>
            <li
              onClick={(e) =>
                addCell(
                  e,
                  props.rowIndex,
                  props.cellIndex,
                  "left",
                  props.data.id,
                  props.row.id
                )
              }
            >
              Add cell left
            </li>
            <li onClick={(e) =>
                deleteCell(
                  e,
                  props.rowIndex,
                  props.cellIndex,
                  "delete",
                  props.data.id,
                  props.row.id
                )
              }>Delete cell</li>
          </ul>
          <h4>Rows</h4>
          <ul>
            <li
              onClick={() => {
                props.addRow();
                hideOptionsHandler();
              }}
            >
              Add row
            </li>
            <li
              onClick={() => {
                props.deleteRow();
                hideOptionsHandler();
              }}
            >
              Delete row
            </li>
          </ul>
        </div>
      </Dropdown> */}
    </>
  );
};

export default Cell;
